import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { connect } from 'react-redux'
import Alert from '../../../../../itrust_common/components/Alert'
import CurrencyTypeView from '../../../../../itrust_common/components/others/CurrencyTypeView'
import TooltipComponent from '../../../../../itrust_common/components/others/TooltipComponent'
import { dateTimeFormat } from '../../../../../itrust_common/components/HelperFunctions'
import {
  orderAppliedWarrantiesLoad,
  orderWarrantyLoad,
  orderWarrantyUse,
  orderWarrantyDelete,
  orderAppliedWarrantiesReset,
} from '../../../../../redux/actions/order/orderWarrantyActions'
import useInfiniteScroll, { hasMorePages } from './useInfiniteScroll'
import './WarrantyModal.scss'

const RPP = 10

const joinMeta = (...parts) => parts.filter(Boolean)

const ChargeValue = ({ type, value }) => {
  if (type === 'free') return 'Free'
  if (value == null) return type || null
  if (type === 'percent') return `${value}%`
  return <CurrencyTypeView amount={value} />
}

function UseWarrantyModal({
  isOpen,
  onClose,
  onSuccess,
  order = {},
  initialWarrantyId = null,
  orderWarranties = [],
  warrantyDetail = null,
  meta = {},
  loading = false,
  detailLoading = false,
  saving = false,
  orderAppliedWarrantiesLoad: loadOrderWarranties,
  orderWarrantyLoad: loadWarrantyDetail,
  orderWarrantyUse: submitWarrantyUse,
  orderWarrantyDelete: deleteWarranty,
  orderAppliedWarrantiesReset: resetOrderWarranties,
}) {
  const [selectedWarrantyId, setSelectedWarrantyId] = useState(null)
  const [selectedReasonIds, setSelectedReasonIds] = useState([])
  const [historyOpen, setHistoryOpen] = useState(false)
  const [page, setPage] = useState(1)
  const leftRef = useRef(null)
  const singleId = initialWarrantyId || null
  const warranties = singleId ? (warrantyDetail ? [warrantyDetail] : []) : orderWarranties
  const listLoading = singleId ? detailLoading : loading
  const hasMore = !singleId && hasMorePages(meta, orderWarranties.length)

  const reasons = warrantyDetail?.reasons_attributes || []
  const histories = warrantyDetail?.histories_attributes || []

  useEffect(() => {
    if (!isOpen || !order?.id) return
    setSelectedWarrantyId(singleId)
    setSelectedReasonIds([])
    setHistoryOpen(false)
    setPage(1)
    if (!singleId) loadOrderWarranties(order.id, { page: 1, rpp: RPP })
    return () => resetOrderWarranties()
  }, [isOpen, order?.id, singleId])

  useEffect(() => {
    if (!isOpen || singleId) return
    if (!orderWarranties.length) return setSelectedWarrantyId(null)
    if (!orderWarranties.some((w) => w.id === selectedWarrantyId)) {
      setSelectedWarrantyId(orderWarranties[0].id)
    }
  }, [isOpen, singleId, orderWarranties, selectedWarrantyId])

  useEffect(() => {
    if (!isOpen || !order?.id || !selectedWarrantyId) return
    setSelectedReasonIds([])
    setHistoryOpen(false)
    loadWarrantyDetail(order.id, selectedWarrantyId)
  }, [isOpen, order?.id, selectedWarrantyId])

  const loadMore = useCallback(() => {
    if (singleId || loading || !hasMore || !order?.id) return
    const nextPage = page + 1
    setPage(nextPage)
    loadOrderWarranties(order.id, { page: nextPage, rpp: RPP })
  }, [singleId, loading, hasMore, page, order?.id, loadOrderWarranties])

  const sentinelRef = useInfiniteScroll({
    rootRef: leftRef,
    hasMore: isOpen && hasMore,
    loading: listLoading,
    onLoadMore: loadMore,
  })

  const toggleReason = (reason) => {
    if (reason.status !== 'available') return

    if (reason.reason_combination === 'none') {
      setSelectedReasonIds((ids) => (ids.includes(reason.id) ? [] : [reason.id]))
      return
    }

    setSelectedReasonIds((ids) => {
      const multiIds = ids.filter((id) => {
        const current = reasons.find((r) => r.id === id)
        return current?.reason_combination !== 'none'
      })
      return multiIds.includes(reason.id)
        ? multiIds.filter((id) => id !== reason.id)
        : [...multiIds, reason.id]
    })
  }

  const handleUse = () => {
    if (order?.status !== 'finalized' || !order?.id || !warrantyDetail?.id || !selectedReasonIds.length) return

    Alert.confirm('Are you sure you want to apply this warranty?', (confirmed) => {
      if (!confirmed) return
      submitWarrantyUse(order.id, warrantyDetail.id, {
        reasons_attributes: selectedReasonIds.map((id) => {
          const reason = reasons.find((r) => r.id === id)
          return { id, used_limit: (Number(reason?.used_limit) || 0) + 1 }
        }),
      }).then(() => {
        onClose()
        if (onSuccess) onSuccess()
      }).catch(() => {})
    })
  }

  const handleDelete = (e, warrantyId) => {
    e.stopPropagation()
    if (!order?.id || !warrantyId || saving) return

    Alert.confirm('Are you sure you want to delete this warranty?', (confirmed) => {
      if (!confirmed) return
      deleteWarranty(order.id, warrantyId)
        .then(() => {
          if (singleId) onClose()
          if (onSuccess) onSuccess()
        })
        .catch(() => {})
    })
  }

  if (!isOpen) return null

  const isOrderFinalized = order?.status === 'finalized'
  const canApplyWarranty = isOrderFinalized
    && warrantyDetail
    && selectedReasonIds.length
    && !saving
    && !detailLoading

  return createPortal(
    <div
      className="warranty-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose() }}
    >
      <div className="warranty-modal">
        <div className="warranty-modal-head">
          <div>
            <h4 className="warranty-modal-title">Apply Warranty</h4>
            <div className="warranty-modal-sub">Select an eyeglass warranty, then choose reasons to apply.</div>
          </div>
          <button type="button" className="close-x" onClick={onClose} disabled={saving}>×</button>
        </div>

        <div className="warranty-modal-body">
          <div className="warranty-panel warranty-panel-left" ref={leftRef}>
            <div className="modal-section-label">Eyeglass Warranties</div>

            {listLoading && !warranties.length ? (
              <div className="text-center py-5 text-muted">
                <i className="la la-spinner la-spin la-2x text-primary" />
              </div>
            ) : !warranties.length ? (
              <div className="text-center py-5 text-muted">No warranties on this order.</div>
            ) : (
              <>
                {warranties.map((warranty) => {
                  const details = warranty.eyeglass_details || {}
                  const frames = details.frames || {}
                  const frameMeta = joinMeta(frames.brand, frames.model, frames.color)
                  const warrantyMeta = joinMeta(
                    warranty.name,
                    warranty.price != null ? <CurrencyTypeView amount={warranty.price} /> : null,
                    warranty.term_value != null && warranty.term_unit
                      ? `${warranty.term_value} ${warranty.term_unit}`
                      : null,
                    warranty.created_at ? dateTimeFormat(warranty.created_at) : null,
                  )

                  return (
                    <div
                      key={warranty.id}
                      className={`warranty-card use-eg-card ${warranty.id === selectedWarrantyId ? 'selected' : ''}`}
                      onClick={() => !singleId && setSelectedWarrantyId(warranty.id)}
                    >
                      <span className="radio" />
                      <div className="warranty-card-content">
                        <div className="d-flex justify-content-between align-items-start">
                          {details.sub_order_id && (
                            <span className="warranty-name">{details.sub_order_id}</span>
                          )}
                          <div className="d-flex align-items-center">
                            {warranty.status && (
                              <span className={`use-status-badge ${warranty.status === 'active' ? 'active' : 'inactive'}`}>
                                {warranty.status}
                              </span>
                            )}
                            <span
                              className={`use-delete-action ml-2 ${saving ? 'disabled' : ''}`}
                              onClick={(e) => handleDelete(e, warranty.id)}
                            >
                              <TooltipComponent title="Delete">
                                <i className="la la-trash text-danger pointer" />
                              </TooltipComponent>
                            </span>
                          </div>
                        </div>

                        {frameMeta.length > 0 && (
                          <div className="use-eg-meta">
                            {frameMeta.map((part, index) => (
                              <React.Fragment key={index}>
                                {index > 0 && <span className="use-dot">·</span>}
                                {part}
                              </React.Fragment>
                            ))}
                          </div>
                        )}

                        {warrantyMeta.length > 0 && (
                          <div className="use-eg-meta">
                            {warrantyMeta.map((part, index) => (
                              <React.Fragment key={index}>
                                {index > 0 && <span className="use-dot">·</span>}
                                <span>{part}</span>
                              </React.Fragment>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}

                {!singleId && loading && orderWarranties.length > 0 && (
                  <div className="text-center py-3 text-muted">
                    <i className="la la-spinner la-spin text-primary" />
                  </div>
                )}
                {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
              </>
            )}
          </div>

          <div className="warranty-panel warranty-panel-right">
            <div className="modal-section-label">Reason for using warranty</div>

            {!selectedWarrantyId ? (
              <div className="text-center py-5 text-muted">Select a warranty.</div>
            ) : detailLoading ? (
              <div className="text-center py-5 text-muted">
                <i className="la la-spinner la-spin la-2x text-primary" />
              </div>
            ) : !reasons.length ? (
              <div className="text-center py-4 text-muted">No reasons on this warranty.</div>
            ) : (
              reasons.map((reason) => {
                const selected = selectedReasonIds.includes(reason.id)
                const available = reason.status === 'available'
                const pricings = reason.pricings_attributes || []

                return (
                  <div
                    key={reason.id}
                    className={`eyeglass-card use-reason-card ${selected ? 'selected' : ''} ${!available ? 'is-disabled' : ''}`}
                    onClick={() => toggleReason(reason)}
                  >
                    <span className="check" />
                    <div className="use-reason-main">
                      <div className="d-flex justify-content-between align-items-start">
                        {reason.name && <div className="eyeglass-title">{reason.name}</div>}
                        <div className="use-reason-badges">
                          {reason.status && (
                            <span className={`use-status-badge ${available ? 'reason-active' : 'inactive'}`}>
                              {reason.status}
                            </span>
                          )}
                          {reason.reason_combination && (
                            <span className="use-combo-badge">{reason.reason_combination}</span>
                          )}
                        </div>
                      </div>

                      <div className="eyeglass-line">
                        {joinMeta(
                          reason.applies_to,
                          (reason.used_limit != null || reason.usage_limit != null)
                            ? `Used ${reason.used_limit} of ${reason.usage_limit}`
                            : null,
                        ).join(' · ')}
                        {!reason.conditional_pricing && reason.charge_type && (
                          <>
                            {' · '}
                            <ChargeValue type={reason.charge_type} value={reason.charge_value} />
                          </>
                        )}
                      </div>

                      {reason.conditional_pricing && pricings.length > 0 && (
                        <div className="use-reason-pricings">
                          {pricings.map((pricing) => (
                            <div
                              key={pricing.id || `${pricing.from_day}-${pricing.to_day}`}
                              className="use-reason-pricing-row"
                            >
                              <span className="use-reason-pricing-days">
                                Day {pricing.from_day}–{pricing.to_day}
                              </span>
                              <span className="use-reason-pricing-charge">
                                <ChargeValue type={pricing.charge_type} value={pricing.charge_value} />
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })
            )}

            {selectedWarrantyId && !detailLoading && (
              <div className={`use-history-box ${historyOpen ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="use-history-toggle"
                  onClick={() => setHistoryOpen((open) => !open)}
                >
                  <span className="use-history-toggle-label">
                    Warranty Use History
                    <span className="use-history-count">{histories.length}</span>
                  </span>
                  <i className={`la ${historyOpen ? 'la-angle-up' : 'la-angle-down'}`} />
                </button>
                {historyOpen && (
                  <div className="use-history-list">
                    {!histories.length ? (
                      <div className="use-history-empty">No usage history yet.</div>
                    ) : (
                      histories.map((history) => {
                        const usage = history.changed_values?.reason_usage || {}
                        return (
                          <div key={history.id} className="use-history-item">
                            <div className="use-history-reason">
                              {usage.reason_name || 'Warranty used'}
                              {usage.applies_to ? ` · ${usage.applies_to}` : ''}
                            </div>
                            {history.created_at && (
                              <div className="use-history-date">{dateTimeFormat(history.created_at)}</div>
                            )}
                          </div>
                        )
                      })
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="warranty-modal-foot">
          <div className="foot-note">
            {selectedReasonIds.length
              ? `${selectedReasonIds.length} selected`
              : 'Select at least one available reason'}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <TooltipComponent
              title={!isOrderFinalized ? 'Order must be finalized to apply warranty' : ''}
            >
              <span className="d-inline-block">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUse}
                  disabled={!canApplyWarranty}
                >
                  {saving ? 'Applying...' : 'Apply Warranty'}
                </button>
              </span>
            </TooltipComponent>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

const mapStateToProps = (state) => {
  const s = state.orderWarranty || {}
  return {
    orderWarranties: s.orderWarranties || [],
    warrantyDetail: s.orderWarrantyDetail || null,
    meta: s.orderWarrantiesMeta || {},
    loading: Boolean(s.orderWarrantiesLoading),
    detailLoading: Boolean(s.orderWarrantyDetailLoading),
    saving: Boolean(s.saving),
  }
}

export default connect(mapStateToProps, {
  orderAppliedWarrantiesLoad,
  orderWarrantyLoad,
  orderWarrantyUse,
  orderWarrantyDelete,
  orderAppliedWarrantiesReset,
})(UseWarrantyModal)
